import { useEffect, useMemo, useState, useCallback, Fragment } from 'react';
import DashboardLayout from '@/components/feature/DashboardLayout';
import { API_BASE_URL, authFetch } from '@/services/api';
import {
  ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from 'recharts';
import { useTheme } from '@/hooks/useTheme';
import { useSelectedAsset } from '@/hooks/useSelectedAsset';

const textMuted = '#6B6B70';
const accent = '#4F9DFF';
const green = '#39D98A';
const red = '#FF6B6B';
const amber = '#F5A623';

interface Asset { id: string; assetName: string; eicCode: string; installedCapacityMw: string | number; region?: string; operationalStatus: string; }
type Mode = 'charge' | 'discharge' | 'idle';

const num = (n: any, d = 0, suf = '') =>
  n == null || isNaN(Number(n)) ? '—'
    : `${Number(n).toLocaleString('uk-UA', { minimumFractionDigits: d, maximumFractionDigits: d })}${suf ? ' ' + suf : ''}`;
const short = (s: string) => (s || '').replace(/^СЕС\s+/, '').trim();
const tomorrow = () => new Date(Date.now() + 864e5).toLocaleDateString('en-CA');
const fmtKyiv = (iso: string | null) =>
  iso ? new Date(iso).toLocaleString('uk-UA', { timeZone: 'Europe/Kyiv', day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';
const longDate = (ymd: string) => {
  const d = new Date(ymd + 'T00:00:00');
  return d.toLocaleDateString('uk-UA', { day: 'numeric', month: 'long', year: 'numeric' });
};
const shortDate = (ymd: string) => ymd.split('-').reverse().join('.');
const monthLabel = (m: string) => {
  const [y, mo] = m.split('-').map(Number);
  const s = new Date(y, mo - 1, 1).toLocaleDateString('uk-UA', { month: 'long', year: 'numeric' });
  return s.charAt(0).toUpperCase() + s.slice(1);
};

// Card is defined OUTSIDE the page component so it is a STABLE component type.
// (Previously it was declared inline, which remounted the whole subtree on every
//  render — that stole focus from inputs after one keystroke and scrolled to top.)
function Card({ title, subtitle, children, right, tk }: any) {
  return (
    <div style={{ background: tk.cardBg, border: `1px solid ${tk.cardBorder}`, borderRadius: 12, padding: 18, marginBottom: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12, gap: 12, flexWrap: 'wrap' }}>
        <div>
          <div style={{ fontSize: 15, fontWeight: 600, color: tk.textMain }}>{title}</div>
          {subtitle && <div style={{ fontSize: 12, color: textMuted, marginTop: 2 }}>{subtitle}</div>}
        </div>
        {right}
      </div>
      {children}
    </div>
  );
}

export default function ComDispPage() {
  const { isDark } = useTheme();
  const cardBg = isDark ? '#1A1A1A' : '#FFFFFF';
  const cardBorder = isDark ? 'rgba(255,255,255,0.07)' : '#E5E5E8';
  const textMain = isDark ? '#E0E0E0' : '#1A1A1A';
  const innerBg = isDark ? '#0F0F0F' : '#F5F5F7';
  const rowBorder = isDark ? 'rgba(255,255,255,0.04)' : '#EEF0F2';
  const codeColor = isDark ? '#9AD' : '#2563EB';
  const tk = { cardBg, cardBorder, textMain };

  const { selectedAsset, setSelectedAsset } = useSelectedAsset();
  const assetId = selectedAsset?.id || '';
  const [assets, setAssets] = useState<Asset[]>([]);
  const [date, setDate] = useState(tomorrow());
  const [month, setMonth] = useState(new Date().toLocaleDateString('en-CA').slice(0, 7));

  const [net, setNet] = useState<any>(null);
  const [mms, setMms] = useState<any>(null);
  const [rev, setRev] = useState(1);
  const [bess, setBess] = useState<Record<number, { mode: Mode; powerKw: number }>>({});
  const [bessCfg, setBessCfg] = useState<any>(null);
  const [bessDiag, setBessDiag] = useState<any>(null);
  const [agentStatus, setAgentStatus] = useState<any>(null);
  const [balance, setBalance] = useState<any>(null);
  const [imbPriced, setImbPriced] = useState<any>(null);
  const [lost, setLost] = useState<any>(null);
  const [xmlOpen, setXmlOpen] = useState(false);
  const [busy, setBusy] = useState('');
  const [toast, setToast] = useState<{ kind: 'ok' | 'warn' | 'err'; msg: string } | null>(null);
  const [openDay, setOpenDay] = useState<string | null>(null);
  const [dayHours, setDayHours] = useState<Record<string, any[]>>({});

  const api = useCallback(async (path: string, init?: RequestInit) => {
    const r = await authFetch(`${API_BASE_URL}${path}`, init);
    const j = await r.json().catch(() => ({}));
    if (!r.ok) throw Object.assign(new Error(j.message || j.error || r.statusText), { data: j, status: r.status });
    return j;
  }, []);

  const flash = (kind: 'ok' | 'warn' | 'err', msg: string) => { setToast({ kind, msg }); setTimeout(() => setToast(null), 6000); };

  const pickAsset = (a: Asset) => setSelectedAsset({
    id: a.id, assetName: a.assetName, installedCapacityMw: Number(a.installedCapacityMw) || 0,
    region: a.region || '', operationalStatus: a.operationalStatus || 'active',
  });

  // load assets once; keep the globally selected station (persists across menu items)
  useEffect(() => {
    authFetch(`${API_BASE_URL}/admin/assets`).then((r) => r.json()).then((a: Asset[]) => {
      const live = (a || []).filter((x) => x.eicCode);
      setAssets(live);
      if (live.length && !selectedAsset) pickAsset(live[0]);
    }).catch(() => {});
  }, []); // eslint-disable-line

  const loadForDate = useCallback(async () => {
    if (!assetId) return;
    try {
      const [n, m, b, ag] = await Promise.all([
        api(`/comdisp/net-forecast?assetId=${assetId}&date=${date}`),
        api(`/comdisp/mms/schedule?assetId=${assetId}&date=${date}`).catch(() => null),
        api(`/comdisp/bess-schedule?assetId=${assetId}&date=${date}`),
        api(`/comdisp/bess-agent/status?assetId=${assetId}`).catch(() => null),
      ]);
      setNet(n); setMms(m); setBessCfg(b.config || null); setAgentStatus(ag);
      const bm: Record<number, { mode: Mode; powerKw: number }> = {};
      (b.hours || []).forEach((h: any) => { bm[h.hour] = { mode: h.mode, powerKw: h.powerKw }; });
      setBess(bm);
    } catch (e: any) { flash('err', e.message); }
  }, [assetId, date, api]);

  const loadMonthly = useCallback(async () => {
    if (!assetId) return;
    const to = new Date().toLocaleDateString('en-CA');
    const from = new Date(Date.now() - 29 * 864e5).toLocaleDateString('en-CA');
    try {
      const [bal, ip, lg] = await Promise.all([
        api(`/comdisp/balance?assetId=${assetId}&month=${month}-01`),
        api(`/comdisp/imbalance-priced?assetId=${assetId}&from=${from}&to=${to}`),
        api(`/comdisp/lost-generation?assetId=${assetId}&from=${from}&to=${to}`),
      ]);
      setBalance(bal); setImbPriced(ip); setLost(lg); setOpenDay(null); setDayHours({});
    } catch (e: any) { flash('err', e.message); }
  }, [assetId, month, api]);

  useEffect(() => { loadForDate(); }, [loadForDate]);
  useEffect(() => { loadMonthly(); }, [loadMonthly]);

  const toggleDay = async (day: string) => {
    if (openDay === day) { setOpenDay(null); return; }
    setOpenDay(day);
    if (!dayHours[day]) {
      try {
        const r = await api(`/comdisp/balance-hours?assetId=${assetId}&day=${day}`);
        setDayHours((p) => ({ ...p, [day]: r.hours || [] }));
      } catch (e: any) { flash('err', e.message); }
    }
  };

  const chartData = useMemo(() => (net?.hours || []).map((h: any, i: number) => ({
    hour: String(i + 1).padStart(2, '0'),
    sun: Number(h.pvKwh) / 1000,
    net: Number(h.netKwh) / 1000,
    charge: -Number(h.chargeKwh) / 1000,
    discharge: Number(h.dischargeKwh) / 1000,
  })), [net]);

  // Fool-proof УЗЕ: перевірка потужності на годину + баланс SoC (старт доби = 0, порожня).
  const bessCheck = useMemo(() => {
    const maxP = bessCfg?.maxPowerKw ?? Infinity;
    const cap = bessCfg?.usableKwh ?? Infinity;
    const errByHour: Record<number, string> = {};
    const msgs: string[] = [];
    let soc = 0;
    for (let h = 1; h <= 24; h++) {
      const cell = bess[h] || { mode: 'idle' as Mode, powerKw: 0 };
      const p = Math.max(0, Number(cell.powerKw) || 0);
      const hh = String(h).padStart(2, '0');
      if (p > maxP + 1e-6) { errByHour[h] = `Потужність ${p} > ${maxP} кВт`; msgs.push(`Год ${hh}: потужність ${p} кВт перевищує ліміт ${maxP} кВт`); }
      if (cell.mode === 'charge') {
        soc += p;
        if (soc > cap + 1e-6) { errByHour[h] = errByHour[h] || `Перевищено ємність (${cap} кВт·год)`; msgs.push(`Год ${hh}: заряд перевищує корисну ємність УЗЕ (${cap} кВт·год)`); soc = cap; }
      } else if (cell.mode === 'discharge') {
        soc -= p;
        if (soc < -1e-6) { errByHour[h] = errByHour[h] || 'Розряд перевищує накопичене'; msgs.push(`Год ${hh}: розряд перевищує накопичений заряд УЗЕ`); soc = 0; }
      }
    }
    return { errByHour, msgs };
  }, [bess, bessCfg]);

  const setBessHour = (hour: number, patch: Partial<{ mode: Mode; powerKw: number }>) =>
    setBess((p) => ({ ...p, [hour]: { mode: 'idle', powerKw: 0, ...p[hour], ...patch } }));

  const saveBess = async () => {
    setBusy('bess');
    try {
      const hours = Object.entries(bess).map(([h, v]) => ({ hour: Number(h), mode: v.mode, powerKw: v.powerKw }));
      await api('/comdisp/bess-schedule', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ assetId, date, setBy: 'owner-ui', hours }),
      });
      flash('ok', 'Графік УЗЕ збережено'); await loadForDate();
    } catch (e: any) { flash('err', e.message); } finally { setBusy(''); }
  };

  const autofillBess = async () => {
    setBusy('autofill');
    try {
      const r = await api('/comdisp/bess-autofill', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ assetId, date }),
      });
      const bm: Record<number, { mode: Mode; powerKw: number }> = {};
      (r.hours || []).forEach((h: any) => { bm[h.hour] = { mode: h.mode, powerKw: h.powerKw }; });
      setBess(bm); setBessDiag(r);
      flash(r.socSource === 'live' ? 'ok' : 'warn', r.note);
    } catch (e: any) { flash(e.status === 404 ? 'warn' : 'err', e.message); } finally { setBusy(''); }
  };

  const genXml = async () => {
    setBusy('gen');
    try {
      const r = await api('/comdisp/mms/generate', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ assetId, date, revision: rev }),
      });
      flash('ok', `XML сформовано: ${r.mrid} (${num(r.totalEnergyMwh, 2, 'МВт·год')})`);
      await loadForDate();
    } catch (e: any) {
      flash(e.status === 409 ? 'warn' : 'err', e.message);
    } finally { setBusy(''); }
  };

  const downloadXml = () => {
    if (!mms?.xml) { flash('warn', 'Спершу сформуйте XML'); return; }
    const blob = new Blob([mms.xml], { type: 'application/xml' });
    const u = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = u; a.download = `${mms.mrid || 'mms'}.xml`; a.click();
    URL.revokeObjectURL(u);
  };

  const submitMms = async () => {
    if (!mms?.id) { flash('warn', 'Спершу сформуйте XML'); return; }
    setBusy('submit');
    try {
      const r = await api('/comdisp/mms/submit', {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ id: mms.id }),
      });
      flash('ok', r.note || 'Поставлено в чергу на відправку до MMS.');
      setBusy('');
      await loadForDate();
      // Host submitter runs on a ~2-min timer; refresh a few times to catch queued → submitted/error.
      for (let i = 0; i < 8; i++) { await new Promise((res) => setTimeout(res, 20000)); await loadForDate(); }
    } catch (e: any) { flash('err', e.message); } finally { setBusy(''); }
  };

  const gateInfo = useMemo(() => {
    const dl = mms?.gateDeadline || null;
    return { deadline: dl, past: dl ? new Date(dl).getTime() < Date.now() : false };
  }, [mms]);

  const shiftDate = (days: number) => setDate((d) => new Date(new Date(d + 'T00:00:00').getTime() + days * 864e5).toLocaleDateString('en-CA'));
  const shiftMonth = (delta: number) => setMonth((m) => {
    const [y, mo] = m.split('-').map(Number);
    const d = new Date(y, mo - 1 + delta, 1);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  });

  const btn = (label: string, on: () => void, kind: 'primary' | 'ghost' | 'danger' = 'ghost', disabled = false) => (
    <button onClick={on} disabled={disabled}
      style={{
        padding: '7px 14px', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.5 : 1, border: `1px solid ${kind === 'primary' ? accent : cardBorder}`,
        background: kind === 'primary' ? accent : kind === 'danger' ? 'rgba(255,107,107,0.12)' : 'transparent',
        color: kind === 'primary' ? '#001' : kind === 'danger' ? red : textMain,
      }}>{label}</button>
  );
  const arrow = (label: string, on: () => void) => (
    <button onClick={on} style={{ width: 26, height: 26, borderRadius: 6, cursor: 'pointer', border: `1px solid ${cardBorder}`, background: 'transparent', color: textMain, fontSize: 14, lineHeight: 1 }}>{label}</button>
  );

  const th: any = { textAlign: 'right', padding: '7px 12px', color: textMuted, fontSize: 11, fontWeight: 600, borderBottom: `1px solid ${cardBorder}` };
  const td: any = { textAlign: 'right', padding: '7px 12px', color: textMain, fontSize: 12.5, borderBottom: `1px solid ${rowBorder}` };

  return (
    <DashboardLayout breadcrumb={['Дашборд', 'Диспетчеризація', 'Комерційна диспетчерська']}>
      <div style={{ color: textMain, maxWidth: '100%' }}>
        {/* header: asset + delivery date */}
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center', marginBottom: 16 }}>
          <h1 style={{ fontSize: 20, fontWeight: 700, marginRight: 8 }}>Комерційна диспетчерська</h1>
          <select value={assetId} onChange={(e) => { const a = assets.find((x) => x.id === e.target.value); if (a) pickAsset(a); }}
            style={{ background: innerBg, color: textMain, border: `1px solid ${cardBorder}`, borderRadius: 8, padding: '7px 10px', fontSize: 13 }}>
            {assets.map((a) => <option key={a.id} value={a.id}>{short(a.assetName)}</option>)}
          </select>
          <label style={{ fontSize: 12, color: textMuted }}>Доба поставки:</label>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            {arrow('‹', () => shiftDate(-1))}
            <input type="date" value={date} onChange={(e) => setDate(e.target.value)}
              style={{ background: innerBg, color: textMain, border: `1px solid ${cardBorder}`, borderRadius: 8, padding: '6px 10px', fontSize: 13, colorScheme: isDark ? 'dark' : 'light' }} />
            {arrow('›', () => shiftDate(1))}
          </div>
        </div>

        {/* ── Block 1: net forecast (sun + УЗЕ) ── */}
        <Card tk={tk} title="Підсумковий прогноз (сонячна генерація + УЗЕ)"
          right={<div style={{ fontSize: 12, color: textMuted }}>
            Сонячна {num(net ? net.pvKwh / 1000 : null, 1)} · Заряд −{num(net ? net.chargeKwh / 1000 : null, 1)} · Розряд +{num(net ? net.dischargeKwh / 1000 : null, 1)} · <b style={{ color: green }}>Разом {num(net ? net.netKwh / 1000 : null, 1)} МВт·год</b></div>}>
          <div style={{ height: 240 }}>
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={chartData} margin={{ top: 8, right: 8, bottom: 0, left: 0 }} maxBarSize={28} barCategoryGap="16%">
                <CartesianGrid stroke={cardBorder} vertical={false} />
                <XAxis dataKey="hour" tick={{ fill: textMuted, fontSize: 9 }} interval={0} />
                <YAxis width={46} tick={{ fill: textMuted, fontSize: 10 }} />
                <Tooltip contentStyle={{ background: innerBg, border: `1px solid ${cardBorder}`, borderRadius: 8, fontSize: 12, color: textMain }} formatter={(v: any, n: any) => [`${Number(v).toFixed(4)} МВт·год`, n]} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Bar dataKey="sun" name="Сонячна генерація" fill={accent} opacity={0.55} stackId="net" />
                <Bar dataKey="discharge" name="Розряд УЗЕ" fill={green} opacity={0.7} stackId="net" />
                <Bar dataKey="charge" name="Заряд УЗЕ" fill={amber} opacity={0.7} stackId="net" />
                <Line dataKey="net" name="Підсумок" stroke={green} strokeWidth={2} dot={false} />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
          <div style={{ marginTop: 8, border: `1px solid ${cardBorder}`, borderRadius: 8, overflow: 'hidden' }}>
            <div style={{ display: 'flex', alignItems: 'stretch', borderBottom: `1px solid ${cardBorder}` }}>
              <div style={{ width: 46, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'flex-end', paddingRight: 6, fontSize: 9, color: textMuted, borderRight: `1px solid ${cardBorder}` }}>Год</div>
              <div style={{ flex: 1, display: 'flex', paddingRight: 8 }}>
                {chartData.map((d: any, i: number) => <div key={i} style={{ flex: 1, textAlign: 'center', fontSize: 9, color: textMuted, padding: '3px 0', borderRight: i < chartData.length - 1 ? `1px solid ${cardBorder}` : 'none' }}>{d.hour}</div>)}
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'stretch' }}>
              <div style={{ width: 46, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'flex-end', paddingRight: 6, fontSize: 9, color: textMuted, borderRight: `1px solid ${cardBorder}` }}>МВт·год</div>
              <div style={{ flex: 1, display: 'flex', paddingRight: 8 }}>
                {chartData.map((d: any, i: number) => <div key={i} style={{ flex: 1, textAlign: 'center', fontSize: 8, color: d.net < 0 ? amber : textMain, padding: '3px 0', borderRight: i < chartData.length - 1 ? `1px solid ${cardBorder}` : 'none' }}>{d.net.toFixed(2)}</div>)}
              </div>
            </div>
          </div>
        </Card>

        {/* ── Block 2: MMS ── */}
        <Card tk={tk} title="Графік до MMS"
          subtitle={mms ? `${mms.mrid} · контрагент ${mms.counterparty} · ${num(mms.totalEnergyMwh, 2, 'МВт·год')}` : 'Ще не сформовано на цю добу'}
          right={<div style={{ display: 'flex', gap: 6 }}>
            {[1, 2, 3].map((v) => (
              <button key={v} onClick={() => setRev(v)}
                style={{ padding: '5px 10px', borderRadius: 6, fontSize: 12, cursor: 'pointer', border: `1px solid ${rev === v ? accent : cardBorder}`, background: rev === v ? 'rgba(79,157,255,0.15)' : 'transparent', color: rev === v ? accent : textMuted }}>
                Верс. {v}</button>))}
          </div>}>
          <div style={{ background: gateInfo.past ? 'rgba(255,107,107,0.1)' : 'rgba(79,157,255,0.08)', border: `1px solid ${gateInfo.past ? red : accent}`, borderRadius: 8, padding: '8px 12px', fontSize: 12, marginBottom: 12, color: gateInfo.past ? red : textMain }}>
            {mms?.gateDeadline
              ? <>Версію {mms.revision || rev} потрібно завантажити на MMS <b>до {fmtKyiv(mms.gateDeadline)}</b>{gateInfo.past ? ' — дедлайн минув!' : ''}.</>
              : <>Дедлайни версій (D-1): верс.1 до 10:00 · верс.2 до 14:00 · верс.3 до 18:00. Графік формується лише на день D+1.</>}
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 10 }}>
            {btn(busy === 'gen' ? 'Формування…' : 'Сформувати XML', genXml, 'primary', busy === 'gen')}
            {btn('Завантажити XML', downloadXml, 'ghost', !mms?.xml)}
            {btn('Відправити на MMS', submitMms, 'ghost', !mms?.id || busy === 'submit')}
            {mms?.xml && btn(xmlOpen ? 'Сховати XML' : 'Показати XML', () => setXmlOpen(!xmlOpen))}
          </div>
          {mms?.status && mms.status !== 'generated' && (() => {
            const m: Record<string, { c: string; t: string }> = {
              queued: { c: amber, t: '⏳ У черзі на відправку до MMS…' },
              submitted: { c: '#37c871', t: '✅ Відправлено. Очікує підтвердження валідації (Acknowledgement за mRID).' },
              error: { c: red, t: `⚠ Помилка відправки: ${mms.errorMessage || ''}` },
              blocked: { c: amber, t: `⚠ ${mms.errorMessage || 'Відправку заблоковано'}` },
            };
            const s = m[mms.status] || { c: textMuted, t: `Статус: ${mms.status}` };
            return <div style={{ fontSize: 12, color: s.c, marginBottom: 8 }}>{s.t}</div>;
          })()}
          {xmlOpen && mms?.xml &&
            <pre style={{ background: innerBg, border: `1px solid ${cardBorder}`, borderRadius: 8, padding: 12, fontSize: 11, color: codeColor, overflowX: 'auto', maxHeight: 320 }}>{mms.xml}</pre>}
        </Card>

        {/* ── УЗЕ editor ── */}
        <Card tk={tk} title="Графік заряду/розряду УЗЕ"
          subtitle="Заряд зменшує, розряд збільшує підсумковий прогноз"
          right={<div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
            <button onClick={autofillBess} disabled={busy === 'autofill' || !bessCfg}
              title="Автозаповнення графіка УЗЕ (AI)"
              style={{ width: 34, height: 34, borderRadius: 8, fontSize: 12, fontWeight: 700, lineHeight: 1,
                cursor: (busy === 'autofill' || !bessCfg) ? 'not-allowed' : 'pointer', opacity: (busy === 'autofill' || !bessCfg) ? 0.5 : 1,
                border: `1px solid ${accent}`, background: 'transparent', color: accent,
                display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              {busy === 'autofill' ? '…' : 'AI'}
            </button>
            {btn(busy === 'bess' ? 'Збереження…' : 'Зберегти графік УЗЕ', saveBess, 'primary', busy === 'bess' || bessCheck.msgs.length > 0)}
          </div>}>
          {agentStatus && (agentStatus.status === 'emergency' || agentStatus.status === 'undercharge') && (
            <div style={{ background: agentStatus.status === 'emergency' ? 'rgba(255,107,107,0.12)' : 'rgba(245,166,35,0.12)', border: `1px solid ${agentStatus.status === 'emergency' ? red : amber}`, borderRadius: 8, padding: '8px 12px', fontSize: 12, color: agentStatus.status === 'emergency' ? red : amber, marginBottom: 10 }}>
              🛡 Агент-запобіжник: {agentStatus.message}
            </div>
          )}
          {bessCheck.msgs.length > 0 && (
            <div style={{ background: 'rgba(255,107,107,0.1)', border: `1px solid ${red}`, borderRadius: 8, padding: '8px 12px', fontSize: 12, color: red, marginBottom: 10 }}>
              ⚠ Технічно неможливі значення — виправте, щоб зберегти:
              <ul style={{ margin: '4px 0 0 16px', padding: 0 }}>{bessCheck.msgs.slice(0, 6).map((m: string, i: number) => <li key={i}>{m}</li>)}</ul>
              {bessCheck.msgs.length > 6 && <div style={{ marginTop: 2 }}>…та ще {bessCheck.msgs.length - 6}</div>}
            </div>
          )}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(12, minmax(0, 1fr))', gap: 6 }}>
            {Array.from({ length: 24 }, (_, i) => i + 1).map((h) => {
              const cur = bess[h] || { mode: 'idle' as Mode, powerKw: 0 };
              const err = bessCheck.errByHour[h];
              const col = err ? red : cur.mode === 'charge' ? amber : cur.mode === 'discharge' ? green : cardBorder;
              return (
                <div key={h} title={err || undefined} style={{ border: `1px solid ${col}`, borderRadius: 6, padding: 6, background: err ? 'rgba(255,107,107,0.08)' : innerBg }}>
                  <div style={{ fontSize: 10, color: textMuted, marginBottom: 3 }}>{String(h).padStart(2, '0')}</div>
                  <select value={cur.mode} onChange={(e) => setBessHour(h, { mode: e.target.value as Mode })}
                    style={{ width: '100%', background: innerBg, color: textMain, border: `1px solid ${cardBorder}`, borderRadius: 4, fontSize: 11, padding: '2px' }}>
                    <option value="idle">—</option>
                    <option value="charge">Заряд</option>
                    <option value="discharge">Розряд</option>
                  </select>
                  <input type="number" value={cur.powerKw || ''} placeholder="кВт" min={0} step={10}
                    onChange={(e) => setBessHour(h, { powerKw: Math.max(0, Number(e.target.value)) })}
                    style={{ width: '100%', marginTop: 3, background: innerBg, color: textMain, border: `1px solid ${cardBorder}`, borderRadius: 4, fontSize: 11, padding: '2px 4px' }} />
                </div>
              );
            })}
          </div>
        </Card>

        {/* ── Blocks 3 + 4/5 + 6 — three widgets, each 1/3 page width ── */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 16, alignItems: 'start' }}>
        <Card tk={tk} title="Баланс (з початку місяця)"
          right={<div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {arrow('‹', () => shiftMonth(-1))}
            <span style={{ fontSize: 13, fontWeight: 600, minWidth: 120, textAlign: 'center' }}>{monthLabel(month)}</span>
            {arrow('›', () => shiftMonth(1))}
          </div>}>
          <div style={{ display: 'flex', gap: 14, marginBottom: 12, flexWrap: 'wrap' }}>
            <div style={{ flex: 1, minWidth: 120, background: innerBg, border: `1px solid ${cardBorder}`, borderRadius: 10, padding: '7px 12px' }}>
              <div style={{ fontSize: 12, color: textMuted }}>Згенеровано</div>
              <div style={{ fontSize: 16, fontWeight: 700, color: green }}>{num(balance ? balance.generatedMtdKwh / 1000 : null, 1, 'МВт·год')}</div>
            </div>
            <div style={{ flex: 1, minWidth: 120, background: innerBg, border: `1px solid ${cardBorder}`, borderRadius: 10, padding: '7px 12px' }}>
              <div style={{ fontSize: 12, color: textMuted }}>Передано в мережу</div>
              <div style={{ fontSize: 16, fontWeight: 700 }}>{num(balance ? balance.deliveredMtdKwh / 1000 : null, 1, 'МВт·год')}</div>
            </div>
          </div>
          {balance?.deliveredSource === 'modbus_proxy' && <div style={{ fontSize: 11, color: amber, marginBottom: 8 }}>ⓘ «Передано в мережу» — попередньо за даними Modbus (комерційний лічильник ще не під'єднано)</div>}
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr>
              <th style={{ ...th, textAlign: 'left' }}>Дата</th>
              <th style={{ ...th, whiteSpace: 'nowrap' }}>Вироблено, МВт·год</th>
              <th style={{ ...th, whiteSpace: 'nowrap' }}>Передано, МВт·год</th>
              <th style={{ ...th, width: 36 }}></th>
            </tr></thead>
            <tbody>{(balance?.days || []).slice().reverse().map((d: any) => {
              const isOpen = openDay === d.day;
              return (
                <Fragment key={d.day}>
                  <tr onClick={() => toggleDay(d.day)} style={{ cursor: 'pointer' }}>
                    <td style={{ ...td, textAlign: 'left' }}>{shortDate(d.day)}</td>
                    <td style={td}>{num(d.generatedKwh / 1000, 1)}</td>
                    <td style={{ ...td, color: d.deliveredSource === 'meter' ? textMain : textMuted }}
                      title={d.deliveredSource === 'meter' ? 'Комерційний лічильник' : 'Попередньо за Modbus'}>
                      {num(d.deliveredKwh / 1000, 1)}{d.deliveredSource !== 'meter' ? ' *' : ''}</td>
                    <td style={{ ...td, textAlign: 'center', color: textMuted }}>{isOpen ? '⌄' : '›'}</td>
                  </tr>
                  {isOpen && (
                    <tr>
                      <td colSpan={4} style={{ padding: 0, background: innerBg }}>
                        <div style={{ padding: '6px 12px' }}>
                          <div style={{ fontSize: 11, color: textMuted, marginBottom: 4 }}>Деталізація по годинах — {longDate(d.day)}</div>
                          {!dayHours[d.day] ? <div style={{ fontSize: 12, color: textMuted, padding: 6 }}>Завантаження…</div> : (
                            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                              <thead><tr><th style={{ ...th, textAlign: 'left' }}>Година</th><th style={th}>Вироблено, кВт·год</th><th style={th}>Передано, кВт·год</th></tr></thead>
                              <tbody>{dayHours[d.day].map((h: any) => (
                                <tr key={h.hour}><td style={{ ...td, textAlign: 'left' }}>{h.hour}</td><td style={td}>{num(h.generatedKwh, 0)}</td><td style={td}>{num(h.deliveredKwh, 0)}</td></tr>))}</tbody>
                            </table>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}</tbody>
          </table>
        </Card>

          <Card tk={tk} title="Небаланси та вартість (БР)" subtitle="Прогноз − факт · × ціна балансуючого ринку · 30 днів">
            <div style={{ display: 'flex', gap: 14, marginBottom: 12, flexWrap: 'wrap' }}>
              <div style={{ flex: '0 1 calc(50% - 7px)', minWidth: 120, background: innerBg, border: `1px solid ${cardBorder}`, borderRadius: 10, padding: '7px 12px' }}>
                <div style={{ fontSize: 12, color: textMuted }}>Врегулювання</div>
                <div style={{ fontSize: 16, fontWeight: 700, color: (imbPriced?.totalSettlementUah || 0) < 0 ? red : green }}>{num(imbPriced?.totalSettlementUah, 0, 'грн')}</div>
              </div>
            </div>
            <div style={{ maxHeight: 440, overflowY: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead><tr><th style={{ ...th, textAlign: 'left' }}>Дата</th><th style={th}>Небаланс, кВт·год</th><th style={th}>Врегул., грн</th><th style={th}>год</th></tr></thead>
                <tbody>{(imbPriced?.days || []).slice().reverse().map((d: any) => (
                  <tr key={d.day}><td style={{ ...td, textAlign: 'left' }}>{shortDate(d.day)}</td>
                    <td style={{ ...td, color: d.imbalanceKwh < 0 ? red : green }}>{num(d.imbalanceKwh, 0)}</td>
                    <td style={{ ...td, color: d.settlementUah < 0 ? red : green }}>{num(d.settlementUah, 0)}</td>
                    <td style={{ ...td, color: textMuted }}>{d.hoursPriced}/{d.hoursTotal}</td></tr>))}</tbody>
              </table>
            </div>
          </Card>

          <Card tk={tk} title="Недогенерація / втрачена вигода" subtitle="Прогноз − факт (з урахуванням ОСР) × РДН · 30 днів">
            <div style={{ display: 'flex', gap: 14, marginBottom: 12, flexWrap: 'wrap' }}>
              <div style={{ flex: 1, minWidth: 120, background: innerBg, border: `1px solid ${cardBorder}`, borderRadius: 10, padding: '7px 12px' }}>
                <div style={{ fontSize: 12, color: textMuted }}>Втрачена вигода</div>
                <div style={{ fontSize: 16, fontWeight: 700, color: red }}>{num(lost?.totalLostRevenueUah, 0, 'грн')}</div>
              </div>
              <div style={{ flex: 1, minWidth: 120, background: innerBg, border: `1px solid ${cardBorder}`, borderRadius: 10, padding: '7px 12px' }}>
                <div style={{ fontSize: 12, color: textMuted }}>ОСР</div>
                <div style={{ fontSize: 16, fontWeight: 700, color: amber }}>{num(lost?.totalLostRevenueOsrUah, 0, 'грн')}</div>
              </div>
            </div>
            <div style={{ maxHeight: 440, overflowY: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead><tr><th style={{ ...th, textAlign: 'left' }}>Дата</th><th style={th}>Недоген., кВт·год</th><th style={th}>Втрата, грн</th><th style={{ ...th, textAlign: 'center' }}>Причина</th></tr></thead>
                <tbody>{(lost?.days || []).slice().reverse().map((d: any) => (
                  <tr key={d.day}><td style={{ ...td, textAlign: 'left' }}>{shortDate(d.day)}</td>
                    <td style={td}>{num(d.shortfallKwh, 0)}</td><td style={{ ...td, color: red }}>{num(d.lostRevenueUah, 0)}</td>
                    <td style={{ ...td, textAlign: 'center' }}>{d.anyConstraint ? <span style={{ fontSize: 10, color: amber, border: `1px solid ${amber}`, borderRadius: 4, padding: '1px 5px' }}>ОСР</span> : <span style={{ color: textMuted }}>—</span>}</td></tr>))}</tbody>
              </table>
            </div>
          </Card>
        </div>
      </div>

      {toast && (
        <div style={{ position: 'fixed', bottom: 24, right: 24, zIndex: 50, maxWidth: 420, padding: '12px 16px', borderRadius: 10, fontSize: 13,
          background: cardBg, boxShadow: '0 6px 24px rgba(0,0,0,0.35)',
          border: `1px solid ${toast.kind === 'ok' ? green : toast.kind === 'warn' ? amber : red}`,
          color: toast.kind === 'ok' ? green : toast.kind === 'warn' ? amber : red }}>{toast.msg}</div>
      )}
    </DashboardLayout>
  );
}
